import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { soapApiLogSettingsKeys, soapCallLogKeys } from "@/lib/query";

/**
 * TanStack Query hooks for the admin "API Logs" settings (Settings →
 * API Logs tab). Mirrors `use-bu-instances.ts`'s query/mutation/toast shape.
 */

export interface SoapApiLogSettings {
  id: string;
  loggingEnabled: boolean;
  successRetentionDays: number;
  /** `null` means error/failed rows are kept forever. */
  errorRetentionDays: number | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface SoapApiLogStats {
  totalRows: number;
  successRows: number;
  errorRows: number;
  successPurgeableNow: number;
  errorPurgeableNow: number;
}

export interface SoapApiLogSettingsInput {
  loggingEnabled: boolean;
  successRetentionDays: number;
  errorRetentionDays: number | null;
}

export interface PurgeResult {
  successDeleted: number;
  errorDeleted: number;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

export function useSoapApiLogSettings() {
  return useQuery({
    queryKey: soapApiLogSettingsKeys.all,
    queryFn: async (): Promise<{ settings: SoapApiLogSettings; stats: SoapApiLogStats }> => {
      const res = await fetch("/api/admin/soap-call-log-settings");
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to fetch API log settings"));
      }
      return res.json();
    },
  });
}

export function useUpdateSoapApiLogSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SoapApiLogSettingsInput): Promise<SoapApiLogSettings> => {
      const res = await fetch("/api/admin/soap-call-log-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to update API log settings"));
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: soapApiLogSettingsKeys.all });
      toast.success("API log settings saved");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update API log settings");
    },
  });
}

export function usePurgeSoapApiCallLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<PurgeResult> => {
      const res = await fetch("/api/admin/soap-call-log-settings/purge", { method: "POST" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Purge failed"));
      const json = await res.json();
      return json.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: soapApiLogSettingsKeys.all });
      queryClient.invalidateQueries({ queryKey: soapCallLogKeys.all });
      toast.success(
        `Purged ${result.successDeleted} success and ${result.errorDeleted} error log rows`,
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Purge failed");
    },
  });
}
